import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getGarmentSpecificationFields, getGarmentTypeConfigs, previewMuslinFormAssist } from '@/lib/api';
import { getMuslinDesignConfig, normalizeMuslinGarmentType } from '@/lib/muslinDesign';
import { AlertTriangle, ArrowLeft, Loader2, Ruler, Sparkles } from 'lucide-react';

const garmentTypeOptions = [
  { value: 'jacket', label: 'Jacket' },
  { value: 'trouser', label: 'Trouser' },
  { value: 'shirt', label: 'Shirt' },
  { value: 'waistcoat', label: 'Waistcoat' },
  { value: 'overcoat', label: 'Overcoat' },
];

const statusOptions = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'retired', label: 'Retired' },
];

const defaultForm = {
  reference_code: '',
  garment_type: '',
  linked_pattern_code: '',
  box_code: '',
  store: '',
  status: 'available',
  notes: '',
  measurement_snapshot: {},
  design_profile: {},
  snapshot_warning_note: '',
};

const assistStatusTone = {
  matched: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  manual_linked_pattern_needed: 'border-amber-200 bg-amber-50 text-amber-700',
};

const designBackfillTone = {
  ready_to_apply: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  manual_review: 'border-amber-200 bg-amber-50 text-amber-700',
  skipped: 'border-slate-200 bg-slate-50 text-slate-600',
  already_complete: 'border-blue-200 bg-blue-50 text-blue-700',
};

const designBackfillLabel = {
  ready_to_apply: 'Design Ready',
  manual_review: 'Manual Review',
  skipped: 'No Safe Fill',
  already_complete: 'Already Filled',
};

const EditableSelectField = ({ field, value, onChange, testIdPrefix }) => {
  const options = field.options || [];
  const isPreset = Boolean(value) && options.includes(value);
  const selectValue = value ? (isPreset ? value : '__custom__') : '__empty__';

  return (
    <div className="space-y-2">
      <Label>{field.label}</Label>
      <Select
        value={selectValue}
        onValueChange={(nextValue) => {
          if (nextValue === '__empty__') {
            onChange('');
            return;
          }
          if (nextValue === '__custom__') {
            onChange(isPreset ? '' : value || '');
            return;
          }
          onChange(nextValue);
        }}
      >
        <SelectTrigger data-testid={`${testIdPrefix}-${field.key}-trigger`}>
          <SelectValue placeholder={`Select ${field.label}`} />
        </SelectTrigger>
        <SelectContent data-testid={`${testIdPrefix}-${field.key}-content`}>
          <SelectItem value="__empty__">Blank</SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option} data-testid={`${testIdPrefix}-${field.key}-option-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {option}
            </SelectItem>
          ))}
          <SelectItem value="__custom__" data-testid={`${testIdPrefix}-${field.key}-option-custom`}>Custom text</SelectItem>
        </SelectContent>
      </Select>
      {!isPreset && (
        <Input
          value={value || ''}
          onChange={(event) => onChange(event.target.value)}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          data-testid={`${testIdPrefix}-${field.key}-custom-input`}
        />
      )}
    </div>
  );
};

const MuslinBasicsFields = ({
  form,
  setForm,
  storeOptions,
  showLinkedPatternField = false,
  onReferenceCodeChange,
  onGarmentTypeChange,
}) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Inventory Details</CardTitle>
      <CardDescription>
        {showLinkedPatternField
          ? 'Linked pattern is optional here. If it is missing, you can still fill the muslin manually.'
          : 'Step 1: enter the basics first, then the app will try to find a matching pattern in step 2.'}
      </CardDescription>
    </CardHeader>
    <CardContent className="grid gap-4 md:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor="muslin-reference-code">Reference / Muslin Code</Label>
        <Input
          id="muslin-reference-code"
          value={form.reference_code}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (onReferenceCodeChange) {
              onReferenceCodeChange(nextValue);
              return;
            }
            setForm((prev) => ({ ...prev, reference_code: nextValue }));
          }}
          data-testid="muslin-inventory-reference-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Garment Type</Label>
        <Select
          value={form.garment_type || ''}
          onValueChange={(value) => {
            if (onGarmentTypeChange) {
              onGarmentTypeChange(value);
              return;
            }
            setForm((prev) => ({ ...prev, garment_type: value }));
          }}
        >
          <SelectTrigger data-testid="muslin-inventory-garment-type-trigger"><SelectValue placeholder="Select garment type" /></SelectTrigger>
          <SelectContent data-testid="muslin-inventory-garment-type-content">
            {garmentTypeOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} data-testid={`muslin-inventory-garment-type-option-${option.value}`}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showLinkedPatternField && (
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="muslin-linked-pattern">Linked Pattern Code</Label>
          <Input
            id="muslin-linked-pattern"
            value={form.linked_pattern_code}
            onChange={(event) => setForm((prev) => ({ ...prev, linked_pattern_code: event.target.value }))}
            placeholder="Fill this only if the app cannot find a pattern from the muslin code"
            data-testid="muslin-inventory-linked-pattern-input"
          />
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="muslin-box-code">Box Code</Label>
        <Input
          id="muslin-box-code"
          value={form.box_code}
          onChange={(event) => setForm((prev) => ({ ...prev, box_code: event.target.value.replace(/[^0-9]/g, '') }))}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Enter box number"
          data-testid="muslin-inventory-box-input"
        />
      </div>
      <div className="space-y-2">
        <Label>Store</Label>
        <Select value={form.store || ''} onValueChange={(value) => setForm((prev) => ({ ...prev, store: value }))}>
          <SelectTrigger data-testid="muslin-inventory-store-trigger"><SelectValue placeholder="Select store" /></SelectTrigger>
          <SelectContent data-testid="muslin-inventory-store-content">
            {storeOptions.map((store) => (
              <SelectItem key={store} value={store} data-testid={`muslin-inventory-store-option-${store.toLowerCase().replace(/\s+/g, '-')}`}>{store}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={form.status || 'available'} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
          <SelectTrigger data-testid="muslin-inventory-status-trigger"><SelectValue placeholder="Select status" /></SelectTrigger>
          <SelectContent data-testid="muslin-inventory-status-content">
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} data-testid={`muslin-inventory-status-option-${option.value}`}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="muslin-notes">Notes</Label>
        <Textarea
          id="muslin-notes"
          value={form.notes}
          onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
          data-testid="muslin-inventory-notes-input"
        />
      </div>
    </CardContent>
  </Card>
);

const MuslinStepSummary = ({ form }) => (
  <Card data-testid="muslin-inventory-step-summary-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Step 1 Summary</CardTitle>
      <CardDescription>The app is now checking whether this muslin code can be matched to an in-app pattern.</CardDescription>
    </CardHeader>
    <CardContent className="grid gap-3 md:grid-cols-4">
      <div data-testid="muslin-inventory-step-summary-code">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Code</div>
        <div className="font-semibold text-slate-900">{form.reference_code || '—'}</div>
      </div>
      <div data-testid="muslin-inventory-step-summary-type">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Type</div>
        <div className="font-semibold capitalize text-slate-900">{form.garment_type || '—'}</div>
      </div>
      <div data-testid="muslin-inventory-step-summary-box">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Box</div>
        <div className="font-semibold text-slate-900">{form.box_code || '—'}</div>
      </div>
      <div data-testid="muslin-inventory-step-summary-store">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Store</div>
        <div className="font-semibold text-slate-900">{form.store || '—'}</div>
      </div>
    </CardContent>
  </Card>
);

const MuslinAssistCard = ({
  assistLoading,
  assistResult,
  form,
  setForm,
  onRetry,
  isEditMode,
}) => {
  const showLinkedPatternFallback = isEditMode || Boolean(assistResult && !assistResult.pattern_exists_in_app);
  const suggestedMeasurementCount = Object.keys(assistResult?.suggested_measurement_snapshot || {}).length;
  const suggestedDesignCount = Object.keys(assistResult?.fillable_design_profile || {}).length;

  return (
    <Card data-testid="muslin-inventory-form-assist-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base"><Sparkles className="h-4 w-4 text-amber-600" /> Step 2 • Pattern Match & Backfill</CardTitle>
        <CardDescription>
          The app checks the muslin code first, then falls back to a linked pattern code only if you need it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {assistLoading ? (
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="muslin-inventory-form-assist-loading-state">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking for an in-app pattern match and reusable design history…
          </div>
        ) : assistResult ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={assistStatusTone[assistResult.status] || assistStatusTone.manual_linked_pattern_needed} data-testid="muslin-inventory-form-assist-status-badge">
                {assistResult.pattern_exists_in_app ? 'Pattern Found' : 'Manual Pattern Fallback'}
              </Badge>
              <Badge className={designBackfillTone[assistResult.design_backfill_preview?.status] || designBackfillTone.skipped} data-testid="muslin-inventory-form-design-status-badge">
                {designBackfillLabel[assistResult.design_backfill_preview?.status] || 'No Safe Fill'}
              </Badge>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-testid="muslin-inventory-form-assist-message">
              {assistResult.message}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="muslin-inventory-form-assist-pattern-summary">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Matched Pattern</div>
                <div className="mt-1 font-semibold text-slate-900">{assistResult.matched_pattern_code || 'No in-app match yet'}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="muslin-inventory-form-assist-measurement-summary">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Measurements Prepared</div>
                <div className="mt-1 font-semibold text-slate-900">{suggestedMeasurementCount} field{suggestedMeasurementCount === 1 ? '' : 's'}</div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid="muslin-inventory-form-assist-design-summary">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Design Fields Filled</div>
                <div className="mt-1 font-semibold text-slate-900">{suggestedDesignCount} field{suggestedDesignCount === 1 ? '' : 's'}</div>
              </div>
            </div>
            {assistResult.design_backfill_preview?.reason && (
              <div className="text-[11px] leading-5 text-slate-500" data-testid="muslin-inventory-form-design-reason">
                {assistResult.design_backfill_preview.reason}
              </div>
            )}
          </div>
        ) : null}

        {showLinkedPatternFallback && (
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="muslin-linked-pattern-step-two">Linked Pattern Code</Label>
              <Input
                id="muslin-linked-pattern-step-two"
                value={form.linked_pattern_code}
                onChange={(event) => setForm((prev) => ({ ...prev, linked_pattern_code: event.target.value }))}
                placeholder="Fill this only if the muslin code does not match an in-app pattern"
                data-testid="muslin-inventory-linked-pattern-input"
              />
            </div>
            <Button type="button" variant="outline" onClick={onRetry} disabled={assistLoading} data-testid="muslin-inventory-form-assist-retry-button">
              {assistLoading ? 'Checking…' : 'Retry Match'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MuslinSnapshotWarning = ({ message }) => {
  if (!message) {
    return null;
  }
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="muslin-inventory-snapshot-warning">
      <AlertTriangle className="mt-0.5 h-4 w-4" />
      <div>{message}</div>
    </div>
  );
};

const MuslinMeasurementSection = ({ measurementFields, form, updateMeasurementField }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base"><Ruler className="h-4 w-4 text-amber-600" /> Muslin Measurement Snapshot</CardTitle>
      <CardDescription>Auto-filled measurements can still be edited manually before you save the muslin.</CardDescription>
    </CardHeader>
    <CardContent>
      {measurementFields.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-600" data-testid="muslin-inventory-measurement-empty-state">
          Select a garment type to load the muslin measurement fields.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="muslin-inventory-measurement-grid">
          {measurementFields.map((field) => (
            <div key={field.field_key} className="space-y-2">
              <Label htmlFor={`muslin-measurement-${field.field_key}`}>{field.field_label}</Label>
              <Input
                id={`muslin-measurement-${field.field_key}`}
                value={form.measurement_snapshot?.[field.field_key] ?? ''}
                onChange={(event) => updateMeasurementField(field.field_key, event.target.value)}
                placeholder="Enter measurement"
                data-testid={`muslin-inventory-measurement-input-${field.field_key}`}
              />
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

const MuslinDesignSection = ({ designFields, form, updateDesignField }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base">Muslin Design Details</CardTitle>
      <CardDescription>Safe backfill fills what it can; you can still adjust every design field manually.</CardDescription>
    </CardHeader>
    <CardContent>
      {designFields.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-600" data-testid="muslin-inventory-design-empty-state">
          Select a garment type to load muslin design details.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2" data-testid="muslin-inventory-design-grid">
          {designFields.map((field) => (
            <EditableSelectField
              key={field.key}
              field={field}
              value={form.design_profile?.[field.key] || ''}
              onChange={(value) => updateDesignField(field.key, value)}
              testIdPrefix="muslin-inventory-design"
            />
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export const MuslinInventoryFormDialog = ({
  open,
  onOpenChange,
  onSubmit,
  stores = [],
  initialValues = {},
  title = 'Add Muslin Inventory Item',
  description = 'Save a reusable muslin/test garment into inventory.',
  submitLabel = 'Save Item',
  saving = false,
}) => {
  const isEditMode = Boolean(initialValues?.muslin_inventory_id);
  const [form, setForm] = useState(defaultForm);
  const [measurementFields, setMeasurementFields] = useState([]);
  const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistResult, setAssistResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...defaultForm,
      ...initialValues,
      box_code: String(initialValues?.box_code || '').replace(/[^0-9]/g, ''),
      status: initialValues?.status || 'available',
      measurement_snapshot: initialValues?.measurement_snapshot || {},
      design_profile: initialValues?.design_profile || {},
      snapshot_warning_note: initialValues?.snapshot_warning_note || '',
    });
    setStep(initialValues?.muslin_inventory_id ? 2 : 1);
    setAssistLoading(false);
    setAssistResult(null);
  }, [open, initialValues]);

  useEffect(() => {
    const loadMeasurementFields = async () => {
      const garmentType = normalizeMuslinGarmentType(form.garment_type);
      if (!garmentType) {
        setMeasurementFields([]);
        return;
      }

      try {
        const configsRes = await getGarmentTypeConfigs();
        const config = (configsRes.data || []).find((entry) => entry.garment_type === garmentType);
        if (!config?.config_id) {
          setMeasurementFields([]);
          return;
        }
        const fieldsRes = await getGarmentSpecificationFields(config.config_id);
        setMeasurementFields((fieldsRes.data || []).filter((field) => field.linked_measurement_field));
      } catch (error) {
        console.error('Failed to load muslin measurement fields:', error);
        setMeasurementFields([]);
      }
    };

    if (open) {
      loadMeasurementFields();
    }
  }, [form.garment_type, open]);

  const storeOptions = useMemo(() => (stores || []).filter(Boolean), [stores]);
  const designFields = useMemo(() => getMuslinDesignConfig(form.garment_type), [form.garment_type]);
  const canContinueStepOne = Boolean(form.reference_code && form.garment_type && form.store);

  const updateMeasurementField = (fieldKey, value) => {
    setForm((prev) => ({
      ...prev,
      measurement_snapshot: {
        ...prev.measurement_snapshot,
        [fieldKey]: value,
      },
    }));
  };

  const updateDesignField = (fieldKey, value) => {
    setForm((prev) => ({
      ...prev,
      design_profile: {
        ...prev.design_profile,
        [fieldKey]: value,
      },
    }));
  };

  const resetAssistDrivenState = () => {
    setAssistResult(null);
    setAssistLoading(false);
  };

  const applyAssistSuggestions = (assistData) => {
    setForm((prev) => {
      const mergedMeasurements = {
        ...(assistData?.suggested_measurement_snapshot || {}),
      };
      Object.entries(prev.measurement_snapshot || {}).forEach(([fieldKey, value]) => {
        if (String(value ?? '').trim() !== '') {
          mergedMeasurements[fieldKey] = value;
        }
      });

      const mergedDesign = {
        ...(assistData?.fillable_design_profile || {}),
      };
      Object.entries(prev.design_profile || {}).forEach(([fieldKey, value]) => {
        if (String(value ?? '').trim() !== '') {
          mergedDesign[fieldKey] = value;
        }
      });

      return {
        ...prev,
        linked_pattern_code: assistData?.linked_pattern_code || prev.linked_pattern_code || '',
        measurement_snapshot: mergedMeasurements,
        design_profile: mergedDesign,
        snapshot_warning_note: assistData?.snapshot_warning_note || '',
      };
    });
  };

  const runAssist = async (linkedPatternOverride = form.linked_pattern_code || null) => {
    setAssistLoading(true);
    try {
      const response = await previewMuslinFormAssist({
        reference_code: form.reference_code,
        garment_type: form.garment_type,
        linked_pattern_code: linkedPatternOverride || null,
        measurement_snapshot: form.measurement_snapshot,
        design_profile: form.design_profile,
      });
      const nextAssistResult = response.data || null;
      setAssistResult(nextAssistResult);
      applyAssistSuggestions(nextAssistResult);
    } catch (error) {
      console.error('Failed to preview muslin form assist:', error);
      setAssistResult({
        status: 'manual_linked_pattern_needed',
        pattern_exists_in_app: false,
        message: error.response?.data?.detail || 'Unable to check the pattern right now. Continue with manual entry if needed.',
        design_backfill_preview: { status: 'skipped', reason: 'The app could not complete the pattern/design check right now.' },
        suggested_measurement_snapshot: {},
        fillable_design_profile: {},
      });
    } finally {
      setAssistLoading(false);
    }
  };

  const handleContinueToStepTwo = async () => {
    setStep(2);
    await runAssist(null);
  };

  const handleSubmit = async () => {
    await onSubmit({
      ...form,
      linked_pattern_code: form.linked_pattern_code || null,
      box_code: form.box_code || null,
      notes: form.notes || null,
      measurement_snapshot: Object.fromEntries(Object.entries(form.measurement_snapshot || {}).filter(([, value]) => String(value || '').trim() !== '')),
      design_profile: Object.fromEntries(Object.entries(form.design_profile || {}).filter(([, value]) => String(value || '').trim() !== '')),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[94vh] max-w-5xl flex-col overflow-hidden" data-testid="muslin-inventory-form-dialog">
        <DialogHeader>
          <DialogTitle data-testid="muslin-inventory-form-title">{title}</DialogTitle>
          <DialogDescription data-testid="muslin-inventory-form-description">{description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto pr-2">
          {isEditMode ? (
            <MuslinBasicsFields form={form} setForm={setForm} storeOptions={storeOptions} showLinkedPatternField />
          ) : step === 1 ? (
            <MuslinBasicsFields
              form={form}
              setForm={setForm}
              storeOptions={storeOptions}
              onReferenceCodeChange={(value) => {
                resetAssistDrivenState();
                setForm((prev) => ({
                  ...prev,
                  reference_code: value,
                  linked_pattern_code: '',
                  measurement_snapshot: {},
                  design_profile: {},
                  snapshot_warning_note: '',
                }));
              }}
              onGarmentTypeChange={(value) => {
                resetAssistDrivenState();
                setForm((prev) => ({
                  ...prev,
                  garment_type: value,
                  linked_pattern_code: '',
                  measurement_snapshot: {},
                  design_profile: {},
                  snapshot_warning_note: '',
                }));
              }}
            />
          ) : (
            <MuslinStepSummary form={form} />
          )}

          {(!isEditMode && step === 2) && (
            <MuslinAssistCard
              assistLoading={assistLoading}
              assistResult={assistResult}
              form={form}
              setForm={setForm}
              onRetry={() => runAssist(form.linked_pattern_code || null)}
              isEditMode={false}
            />
          )}

          {(isEditMode || step === 2) && (
            <>
              <MuslinSnapshotWarning message={form.snapshot_warning_note} />
              <MuslinMeasurementSection measurementFields={measurementFields} form={form} updateMeasurementField={updateMeasurementField} />
              <MuslinDesignSection designFields={designFields} form={form} updateDesignField={updateDesignField} />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="muslin-inventory-cancel-button">Cancel</Button>
          {!isEditMode && step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)} data-testid="muslin-inventory-step-back-button">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          )}
          {!isEditMode && step === 1 ? (
            <Button onClick={handleContinueToStepTwo} disabled={!canContinueStepOne} data-testid="muslin-inventory-step-continue-button">
              Continue to Step 2
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={saving || !form.reference_code || !form.garment_type || !form.store} data-testid="muslin-inventory-submit-button">
              {saving ? 'Saving...' : submitLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};